import { useEffect } from 'react';
import { jt1, rp } from './model';

// A sheet: centred on wide screens, bottom sheet on phones ("like adding an
// event in Calendar", same as the ops app's MxSheet). Esc / backdrop close.
export function Sheet({ title, onClose, children, right }) {
  useEffect(() => {
    const k = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="v2-modal" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="v2-sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="v2-sh"><button type="button" onClick={onClose}>Batal</button><b>{title}</b><span style={{ textAlign: 'right' }}>{right}</span></div>
        <div className="v2-sb">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return <div className="fr"><span className="k">{label}</span>{children}</div>;
}

// Rupiah input: shows thousands separators, stores digits.
export function MoneyInput({ value, onChange, autoFocus }) {
  const shown = value ? Number(String(value).replace(/\D/g, '')).toLocaleString('id-ID') : '';
  return <input className="big" inputMode="numeric" placeholder="0" value={shown} autoFocus={autoFocus} onChange={e => onChange(e.target.value.replace(/\D/g, ''))} />;
}

export function Effect({ label, from, to, note }) {
  const d = to - from;
  return (
    <div className="effect">
      <span>{label}</span>
      {d === 0
        ? <div className="r2"><span style={{ opacity: 1, fontSize: 13.5 }}>{rp(from)}</span><span style={{ opacity: .7, fontSize: 12 }}>tidak berubah</span></div>
        : <div className="r2"><span style={{ opacity: 1, fontSize: 13.5 }}>{rp(from)} → {rp(to)}</span><span style={{ opacity: 1, fontSize: 13, color: d < 0 ? '#F4A3A3' : '#9CD2AE' }}>{d < 0 ? '−' : '+'}{Math.abs(d).toLocaleString('id-ID')}</span></div>}
      {note && <span>{note}</span>}
    </div>
  );
}

export function Toast({ msg, onDone }) {
  useEffect(() => { if (!msg) return; const t = setTimeout(onDone, 4500); return () => clearTimeout(t); }, [msg, onDone]);
  return msg ? <div className="v2-toast" role="status">{msg}</div> : null;
}

export const Pill = ({ kind = 'neutral', children }) => <span className={`pill p-${kind}`}>{children}</span>;

// Daily saldo chart: plan awal (grey dashed), disetujui (ink), diajukan
// (amber dotted, only where it differs). Lowest day marked.
export function DailyChart({ model, height = 220, forecast }) {
  const { v1, settled, tentative, nDays } = model;
  const W = 720, H = height, l = 46, r = 64, t = 14, b = 24;
  const all = v1.concat(settled, tentative, forecast || []);
  const mx = Math.max(...all, 1), mn = Math.min(...all, 0);
  const step = niceStep((mx - mn) / 4);
  const top = Math.ceil(mx / step) * step, bot = Math.floor(mn / step) * step;
  const X = i => l + (nDays === 1 ? 0 : i / (nDays - 1)) * (W - l - r), Y = v => t + (top - v) / (top - bot) * (H - t - b);
  const pts = (arr, f = 0, to = arr.length) => arr.map((v, i) => (i < f || i >= to) ? null : `${X(i).toFixed(1)},${Y(v).toFixed(1)}`).filter(Boolean).join(' ');
  const diffFrom = tentative.findIndex((v, i) => Math.round(v) !== Math.round(settled[i]));
  const ticks = []; for (let v = bot; v <= top + 1; v += step) ticks.push(v);
  const lo = settled.indexOf(Math.min(...settled));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Saldo harian: plan awal, disetujui, kalau pengajuan disetujui">
      {ticks.map(v => <g key={v}><line x1={l} x2={W - r} y1={Y(v)} y2={Y(v)} stroke="#E2D9CC" strokeWidth={v === 0 ? 1.4 : 1} /><text x={l - 6} y={Y(v) + 3} textAnchor="end" fontSize="10" fill="#7A6F63" fontFamily="JetBrains Mono,monospace">{Math.round(v / 1e6)}</text></g>)}
      {[0, 4, 9, 14, 19, 24, nDays - 1].filter(i => i < nDays).map(i => <text key={i} x={X(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="#7A6F63" fontFamily="Inter,sans-serif">{i + 1}</text>)}
      <polyline fill="none" points={pts(v1)} stroke="#B0A594" strokeWidth="1.5" strokeDasharray="4 3" />
      <polyline fill="none" points={pts(settled)} stroke="#2B2420" strokeWidth="2" />
      {forecast && <polyline fill="none" points={pts(forecast)} stroke="#2F6F8F" strokeWidth="1.8" strokeDasharray="6 3" />}
      {diffFrom >= 0 && <polyline fill="none" points={pts(tentative, Math.max(0, diffFrom - 1))} stroke="#C98A2E" strokeWidth="2.6" strokeDasharray="1.5 3.5" strokeLinecap="round" />}
      <circle cx={X(lo)} cy={Y(settled[lo])} r="4" fill="#B91C1C" />
      <text x={X(lo)} y={Y(settled[lo]) - 9} textAnchor="middle" fontSize="10" fill="#B91C1C" fontFamily="Inter,sans-serif">terendah {jt1(settled[lo])} jt · {lo + 1}</text>
      <text x={X(nDays - 1) + 6} y={Y(settled[nDays - 1]) - 4} fontSize="10.5" fontWeight="600" fill="#2B2420" fontFamily="JetBrains Mono,monospace">{jt1(settled[nDays - 1])}</text>
      {diffFrom >= 0 && <text x={X(nDays - 1) + 6} y={Y(tentative[nDays - 1]) + 12} fontSize="10.5" fontWeight="600" fill="#9A5B0A" fontFamily="JetBrains Mono,monospace">{jt1(tentative[nDays - 1])}</text>}
    </svg>
  );
}

export function niceStep(raw) {
  const p = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
  const n = raw / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}

export const ICONS = {
  Ringkasan: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 13.5h3.5v6.5H4.5zM10.25 8.5h3.5v11.5h-3.5zM16 4.5h3.5v15.5H16z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>,
  'Buku Kas': <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M9 9h6M9 13h6" stroke="currentColor" strokeWidth="1.8" /></svg>,
  Perubahan: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 8h11l-3-3M19 16H8l3 3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  Analisis: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5H12z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M14.5 2.8A8.5 8.5 0 0 1 21.2 9.5h-6.7z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>,
  Lainnya: <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="6" cy="12" r="1.8" fill="currentColor" /><circle cx="12" cy="12" r="1.8" fill="currentColor" /><circle cx="18" cy="12" r="1.8" fill="currentColor" /></svg>,
};
